import {
  getEmeraldPrompt,
  getFileTemplate,
  getFolderTemplate,
} from '../constants';

export const getAngularInitialPrompts = (
  html: string,
  css: string,
  version: string,
  styleType: string,
  projectName: string,
  userEmerald = false,
) => [
  {
    content: `** CONTEXT:** Identify reusable code sections from the provided HTML and CSS, and convert them into Angular-specific reusable components. For each identified component, generate the following files:
component-name.component.ts (Component logic)
component-name.component.html (Template)
component-name.component.spec.ts (Unit test)
component-name.component.${styleType} (Style file)
**INFORMATION ABOUT MY INPUT**
HTML Code: ${html}
CSS Code: ${css}
Angular Version: ${version || '16.x'}
Style Type: ${styleType} (e.g., scss, css)
Project Name: ${projectName || 'Test'}
** OBJECTIVES **
Extract reusable UI elements and convert them into multiple Angular components for each repeated code pattern. You can derive component names based on the HTML structure.
You can follow a Component naming convention based on the semantic purpose or visual structure of the HTML elements. use naming convention (e.g., kebab-case).
Some common enhancements you might consider:
Hover effects (e.g., tooltips, color changes)
Toggle states (e.g., active/inactive)
Accessibility features (e.g., ARIA labels)
Keyboard navigation support
Confirmation dialogs before executing actions
Loading indicators for async operations
Add @Input() properties for dynamic content (e.g., text labels), use common default values.
Add @Output() properties for event handling (e.g., click events using $event). Assume a click event for all buttons, icons, etc...
Maintain parent-child relationships between components where applicable.
Ensure code readability by including \n where appropriate.
**OUTPUT FORMAT**  
Prefix each file with '**File:**' followed by the full relative path from the project root.
Do not use numbering for file names.
** RULES:**
Derive component names based on the structure and semantics of the provided HTML.
Use only identified HTML attributes and content for @Input() bindings.
Use $event for all @Output() click events.
Include meaningful and basic test cases in each .spec.ts file.
Maintain a consistent and clean folder structure for all generated files.
Must maintain prefix of each file with '**File:**' followed by the full relative path from the project root path
${userEmerald ? getEmeraldPrompt(html, 'angular')?.prompt : ''}
`,
    role: 'system',
  },
];

export const AngularProjectStructure = (
  projectName: string,
  assets: any,
  styleType: string,
) => {
  const rootFolderPath: any = [
    {
      ...getFolderTemplate(),
      name: projectName,
      open: true,
      children: [
        {
          ...getFolderTemplate(),
          name: 'src',
          open: true,
          children: [
            {
              ...getFolderTemplate(),
              name: 'app',
              open: true,
              children: [],
            },
            ...getAssetsFolderTemplate(assets),
            {
              ...getFileTemplate(),
              name: 'index.html',
              language: 'html',
              extension: 'html',
              content: getIndexHTML(projectName),
            },
            {
              ...getFileTemplate(),
              name: 'main.ts',
              language: 'typescript',
              extension: 'ts',
              content: getMainTsCode(),
            },
            {
              ...getFileTemplate(),
              name: 'styles.${styleType}',
              language: '${styleType}',
              extension: '${styleType}',
              content: '/*Add Global ${styleType}*/',
            },
          ],
        },
        {
          ...getFileTemplate(),
          name: 'angular.json',
          language: 'json',
          extension: 'json',
          content: getAngularJson(projectName, styleType),
        },
        {
          ...getFileTemplate(),
          name: 'package.json',
          language: 'json',
          extension: 'json',
          content: getPackageJSON(projectName),
        },
        {
          ...getFileTemplate(),
          name: 'tsconfig.app.json',
          language: 'json',
          extension: 'json',
          content: getTSAppJson(),
        },
        {
          ...getFileTemplate(),
          name: 'tsconfig.json',
          language: 'json',
          extension: 'json',
          content: getTSConfigJson(),
        },
        {
          ...getFileTemplate(),
          name: 'tsconfig.spec.json',
          language: 'json',
          extension: 'json',
          content: getTSConfigSpecJson(),
        },
      ],
    },
  ];
  // if (!assets || Object.keys(assets).length === 0) {
  //   return rootFolderPath;
  // } else {
  //   rootFolderPath[0].children[0].children[1].children[0].children =
  //     Object.keys(assets).map((eachKey: any) => {
  //       return {
  //         ...getFileTemplate(),
  //         name: eachKey,
  //         language: 'html',
  //         extension: 'svg',
  //         content: assets[eachKey],
  //       };
  //     });
  return rootFolderPath;
  //}
};

export const getAssetsFolderTemplate = (assets: any): any => {
  return {
    ...getFolderTemplate(),
    name: 'assets',
    open: true,
    children: [
      {
        ...getFolderTemplate(),
        name: 'images',
        open: false,
        children:
          !assets || Object.keys(assets).length === 0
            ? []
            : Object.keys(assets).map((eachKey: any) => {
                return {
                  ...getFileTemplate(),
                  name: eachKey,
                  language: 'html',
                  extension: 'svg',
                  content: assets[eachKey],
                };
              }),
      },
      {
        ...getFolderTemplate(),
        name: 'fonts',
        open: false,
        children: [],
      },
    ],
  };
};

export const getIndexHTML = (projectName: string) => {
  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>${projectName}</title>
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <link rel="icon" type="image/x-icon" href="favicon.ico" />
  </head>
  <body>
    <app-root></app-root>
  </body>
</html>`;
};

export const getMainTsCode =
  () => `import { platformBrowserDynamic } from "@angular/platform-browser-dynamic";

import { AppModule } from "./app/app.module";

platformBrowserDynamic()
  .bootstrapModule(AppModule)
  .catch((err) => console.error(err));`;

export const getTSAppJson = () => `{
  "extends": "./tsconfig.json",
  "compilerOptions": {
    "outDir": "./out-tsc/app",
    "types": []
  },
  "files": [
    "src/main.ts"
  ],
  "include": [
    "src/**/*.d.ts"
  ]
}`;

export const getTSConfigJson = () => `{
  "compileOnSave": false,
  "compilerOptions": {
    "outDir": "./dist/out-tsc",
    "strict": true,
    "noImplicitOverride": true,
    "noPropertyAccessFromIndexSignature": true,
    "noImplicitReturns": true,
    "noFallthroughCasesInSwitch": true,
    "skipLibCheck": true,
    "isolatedModules": true,
    "esModuleInterop": true,
    "sourceMap": true,
    "declaration": false,
    "experimentalDecorators": true,
    "moduleResolution": "bundler",
    "importHelpers": true,
    "target": "ES2022",
    "module": "ES2022",
    "lib": [
      "ES2022",
      "dom"
    ]
  },
  "angularCompilerOptions": {
    "enableI18nLegacyMessageIdFormat": false,
    "strictInjectionParameters": true,
    "strictInputAccessModifiers": true,
    "strictTemplates": true
  }
}`;

export const getTSConfigSpecJson = () => `{
  "extends": "./tsconfig.json",
  "compilerOptions": {
    "outDir": "./out-tsc/spec",
    "types": [
      "jasmine"
    ]
  },
  "include": [
    "src/**/*.spec.ts",
    "src/**/*.d.ts"
  ]
}`;

export const getPackageJSON = (projectName: string) => {
  return `{
  "name": "${projectName}",
  "version": "1.0.0",
  "scripts": {
    "ng": "ng",
    "start": "ng serve",
    "build": "ng build",
    "watch": "ng build --watch",
    "test": "ng test"
  },
  "private": true,
  "dependencies": {
    "@angular/animations": "^18.2.0",
    "@angular/common": "^18.2.0",
    "@angular/compiler": "^18.2.0",
    "@angular/core": "^18.2.0",
    "@angular/forms": "^18.2.0",
    "@angular/platform-browser": "^18.2.0",
    "@angular/platform-browser-dynamic": "^18.2.0",
    "@angular/router": "^18.2.0",
    "rxjs": "~7.8.0",
    "tslib": "^2.3.0",
    "zone.js": "~0.14.10"
  },
  "devDependencies": {
    "@angular-devkit/build-angular": "^18.2.6",
    "@angular/cli": "^18.2.6",
    "@angular/compiler-cli": "^18.2.0",
    "@types/color-namer": "^1.3.3",
    "@types/file-saver": "^2.0.7",
    "@types/jasmine": "~5.1.0",
    "@types/prismjs": "^1.26.5",
    "jasmine-core": "~5.2.0",
    "karma": "~6.4.0",
    "karma-chrome-launcher": "~3.2.0",
    "karma-coverage": "~2.2.0",
    "karma-jasmine": "~5.1.0",
    "karma-jasmine-html-reporter": "~2.1.0",
    "typescript": "~5.5.2"
  },
  "browser": {
    "fs": false,
    "path": false,
    "os": false
  }
}
`;
};

export const getAngularJson = (projectName: string, styleType: string) => {
  return `{
  "$schema": "./node_modules/@angular/cli/lib/config/schema.json",
  "version": 1,
  "newProjectRoot": "projects",
  "projects": {
    "${projectName}": {
      "projectType": "application",
      "schematics": {
        "@schematics/angular:component": {
          "style": "${styleType}"
        }
      },
      "root": "",
      "sourceRoot": "src",
      "prefix": "app",
      "architect": {
        "build": {
          "builder": "@angular-devkit/build-angular:application",
          "options": {
            "outputPath": "dist/${projectName}",
            "index": "src/index.html",
            "browser": "src/main.ts",
            "polyfills": ["zone.js"],
            "tsConfig": "tsconfig.app.json",
            "inlineStyleLanguage": "${styleType}",
            "assets": [
              {
                "glob": "**/*",
                "input": "public"
              },
              "src/assets"
            ],
            "styles": ["src/styles.${styleType}"],
            "scripts": []
          },
          "configurations": {
            "production": {
              "budgets": [
                {
                  "type": "initial",
                  "maximumWarning": "500MB",
                  "maximumError": "500MB"
                },
                {
                  "type": "anyComponentStyle",
                  "maximumWarning": "500MB",
                  "maximumError": "500MB"
                }
              ],
              "outputHashing": "none"
            },
            "development": {
              "optimization": false,
              "extractLicenses": false,
              "sourceMap": true
            }
          },
          "single-bundle": {
            "bundleDependencies": "all",
            "vendorChunk": false,
            "commonChunk": false,
            "namedChunks": false
          },
          "defaultConfiguration": "production"
        },
        "serve": {
          "builder": "@angular-devkit/build-angular:dev-server",
          "configurations": {
            "production": {
              "buildTarget": "${projectName}:build:production"
            },
            "development": {
              "buildTarget": "${projectName}:build:development"
            }
          },
          "defaultConfiguration": "development"
        },
        "extract-i18n": {
          "builder": "@angular-devkit/build-angular:extract-i18n"
        },
        "test": {
          "builder": "@angular-devkit/build-angular:karma",
          "options": {
            "polyfills": ["zone.js", "zone.js/testing"],
            "tsConfig": "tsconfig.spec.json",
            "inlineStyleLanguage": "${styleType}",
            "assets": [
              {
                "glob": "**/*",
                "input": "public"
              }
            ],
            "styles": ["src/styles.${styleType}"],
            "scripts": []
          }
        }
      }
    }
  }
}`;
};
